import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { fetchUiPreferences, updateUiPreferences } from '../services/preferencesApi';

export const THEME_STORAGE_KEY = 'darknews-theme';
export const DEFAULT_THEME = 'mountain';

export const THEMES = {
  dark: {
    id: 'dark',
    label: 'Dark',
    description: 'Sophisticated blacks & greys',
    metaColor: '#94a3b8',
  },
  terminal: {
    id: 'terminal',
    label: 'Terminal',
    description: 'Green phosphor — classic DarkNews',
    metaColor: '#32ff6a',
  },
  market: {
    id: 'market',
    label: 'Market',
    description: 'Intelligence desk — blue-grey panels',
    metaColor: '#7eb8ff',
  },
  mountain: {
    id: 'mountain',
    label: 'Mountain',
    description: 'Appalachian forest — moss, bark & slate (default)',
    metaColor: '#7da87d',
  },
  beach: {
    id: 'beach',
    label: 'Beach',
    description: 'Sandy shores — warm dunes & sea glass',
    metaColor: '#e8c9a0',
  },
  brat: {
    id: 'brat',
    label: 'Brat',
    description: '80s/90s candy crush — hot pink & neon',
    metaColor: '#ff2d8a',
  },
};

const VALID_THEME_IDS = Object.keys(THEMES);

export function normalizeThemeId(value) {
  return VALID_THEME_IDS.includes(value) ? value : DEFAULT_THEME;
}

export function readStoredTheme() {
  try {
    return normalizeThemeId(localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    return DEFAULT_THEME;
  }
}

export function themeMetaColor(themeId) {
  const id = normalizeThemeId(themeId);
  return THEMES[id]?.metaColor ?? THEMES[DEFAULT_THEME].metaColor;
}

export function applyThemeToDocument(themeId) {
  const id = normalizeThemeId(themeId);
  document.documentElement.setAttribute('data-theme', id);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    meta.setAttribute('content', themeMetaColor(id));
  }
  return id;
}

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const { authReady, isAuthenticated } = useAuth();
  const [theme, setThemeState] = useState(() => {
    const stored = readStoredTheme();
    applyThemeToDocument(stored);
    return stored;
  });
  const syncedFromServer = useRef(false);

  useEffect(() => {
    if (!authReady || !isAuthenticated || syncedFromServer.current) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const prefs = await fetchUiPreferences();
        if (cancelled || !prefs?.uiTheme) {
          return;
        }
        const id = normalizeThemeId(prefs.uiTheme);
        applyThemeToDocument(id);
        try {
          localStorage.setItem(THEME_STORAGE_KEY, id);
        } catch {
          /* ignore */
        }
        setThemeState(id);
        syncedFromServer.current = true;
      } catch {
        /* localStorage theme remains */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authReady, isAuthenticated]);

  useEffect(() => {
    if (!authReady) {
      return;
    }
    if (!isAuthenticated) {
      syncedFromServer.current = false;
    }
  }, [authReady, isAuthenticated]);

  const setTheme = useCallback(
    (nextTheme) => {
      const id = normalizeThemeId(nextTheme);
      applyThemeToDocument(id);
      try {
        localStorage.setItem(THEME_STORAGE_KEY, id);
      } catch {
        /* localStorage unavailable */
      }
      setThemeState(id);
      if (isAuthenticated) {
        updateUiPreferences(id).catch(() => {
          /* preference sync is best-effort */
        });
      }
    },
    [isAuthenticated],
  );

  const value = useMemo(
    () => ({
      theme,
      themeMeta: THEMES[theme],
      setTheme,
      themes: THEMES,
    }),
    [theme, setTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return ctx;
}
